import { useCallback, useMemo } from 'react';
import { useFeature } from '@optimizely/react-sdk';

import { FeatureFlags } from '@frontend/constants';
import { RAG_TOOL_REQUIREMENTS } from '@frontend/constants/ragToolConfig';
import { Tool } from '@frontend/types';

import { useOperations } from './useOperations';

export function useRAGToolAvailability() {
  const { canInstance } = useOperations();

  const [ragEnabled] = useFeature(FeatureFlags.RAG_ENABLED);

  const flagStates: Record<string, boolean> = useMemo(
    () => ({
      [FeatureFlags.RAG_ENABLED]: ragEnabled,
    }),
    [ragEnabled]
  );

  /**
   * Check if a RAG tool's requirements are satisfied.
   * Returns null if satisfied, or a message if blocked.
   */
  const getRAGBlockReason = useCallback(
    (tool: Tool): string | null => {
      const requirements = RAG_TOOL_REQUIREMENTS[tool.name];
      if (!requirements) return null;

      if (requirements.instanceCapabilities) {
        const hasAll = requirements.instanceCapabilities.every(cap => canInstance([cap], false));
        if (!hasAll) return requirements.disabledMessage;
      }

      if (requirements.optimizelyFlags) {
        const hasAll = requirements.optimizelyFlags.every(flag => flagStates[flag]);
        if (!hasAll) return requirements.disabledMessage;
      }

      return null;
    },
    [canInstance, flagStates]
  );

  return { getRAGBlockReason };
}
